import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { dogCreateSchema, type DogCreate } from '../../../../../packages/contracts/src/index.js';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { FieldError, Input, Label, Select, Textarea } from '../../components/ui/field';
import { CustomerSearchBox } from '../../components/ui/search';
import { api } from '../../shared/api';
import { copy, labelForDogStatus, labelForSex } from '../../shared/i18n';

type DogFormInput = z.input<typeof dogCreateSchema>;
type DogFormOutput = z.output<typeof dogCreateSchema>;

export function DogCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const form = useForm<DogFormInput, unknown, DogFormOutput>({
    resolver: zodResolver(dogCreateSchema),
    defaultValues: { customerId: '', name: '', breed: '', birthDate: '', sex: 'unknown', color: '', vaccinationFileIds: [], sterilized: false, careStatus: 'other', status: 'active' },
  });
  const createDog = useMutation({
    mutationFn: (values: DogCreate) => api.dogs.create(values),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['dogs'] });
      navigate('/admin/dogs');
    },
  });

  return (
    <DogFormShell title={copy.dogs.newDog}>
      <form className="grid gap-3" onSubmit={form.handleSubmit((values) => createDog.mutate(values))}>
        <DogFields form={form} />
        {createDog.isError ? <p className="text-sm text-rose-600">{createDog.error.message}</p> : null}
        <Button type="submit" disabled={createDog.isPending}><Plus className="h-4 w-4" aria-hidden="true" />{copy.dogs.create}</Button>
      </form>
    </DogFormShell>
  );
}

export function DogFormShell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="max-w-2xl space-y-5">
      <div><h1 className="text-2xl font-semibold text-slate-950">{title}</h1><p className="mt-1 text-sm text-slate-500">{copy.dogs.body}</p></div>
      <Card><CardHeader><h2 className="text-sm font-semibold">Perfil</h2></CardHeader><CardContent>{children}</CardContent></Card>
    </div>
  );
}

export function DogFields({ form }: { form: ReturnType<typeof useForm<DogFormInput, unknown, DogFormOutput>> }) {
  return (
    <>
      <div className="grid gap-1.5">
        <Label htmlFor="customerId">{copy.dogs.owner}</Label>
        <CustomerSearchBox id="customerId" value={form.watch('customerId')} required placeholder={copy.dogs.selectOwner} onChange={(customerId) => form.setValue('customerId', customerId, { shouldDirty: true, shouldValidate: true })} />
        <FieldError message={form.formState.errors.customerId?.message} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="grid gap-1.5"><Label htmlFor="name">{copy.common.name}</Label><Input id="name" {...form.register('name')} /><FieldError message={form.formState.errors.name?.message} /></div>
        <div className="grid gap-1.5"><Label htmlFor="breed">{copy.dogs.breed}</Label><Input id="breed" {...form.register('breed')} /></div>
      </div>
      <div className="grid gap-3 sm:grid-cols-1">
        <div className="grid gap-1.5"><Label htmlFor="birthDate">{copy.dogs.birthDate}</Label><Input id="birthDate" type="date" {...form.register('birthDate')} /></div>
        <div className="grid gap-1.5"><Label htmlFor="weightKg">{copy.dogs.weightKg}</Label><Input id="weightKg" type="number" step="0.1" {...form.register('weightKg', { valueAsNumber: true })} /></div>
        <div className="grid gap-1.5"><Label htmlFor="sex">{copy.dogs.sex}</Label><Select id="sex" {...form.register('sex')}><option value="unknown">{labelForSex('unknown')}</option><option value="male">{labelForSex('male')}</option><option value="female">{labelForSex('female')}</option></Select></div>
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="status">{copy.common.status}</Label>
        <Select id="status" {...form.register('status')}>
          <option value="active">{labelForDogStatus('active')}</option>
          <option value="inactive">{labelForDogStatus('inactive')}</option>
          <option value="deceased">{labelForDogStatus('deceased')}</option>
        </Select>
      </div>
      <fieldset className="grid gap-2"><legend className="text-sm font-medium text-[#4f5d49]">Estatus de cuidado</legend><div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">{careStatuses.map((status) => <label key={status.value} className={`cursor-pointer rounded-md border p-2 text-center text-xs font-semibold ${status.className} ${form.watch('careStatus') === status.value ? 'ring-2 ring-[#223126] ring-offset-1' : ''}`}><input className="sr-only" type="radio" value={status.value} {...form.register('careStatus')} />{status.label}</label>)}</div></fieldset>
      <label className="flex items-center gap-2 text-sm font-medium text-[#4f5d49]"><input type="checkbox" {...form.register('sterilized')} />Esterilizado</label>
      <div className="grid gap-1.5"><Label htmlFor="feedingInstructions">{copy.dogs.feedingInstructions}</Label><Textarea id="feedingInstructions" {...form.register('feedingInstructions')} /></div>
      <div className="grid gap-1.5"><Label htmlFor="medicalConditions">{copy.dogs.medicalConditions}</Label><Textarea id="medicalConditions" {...form.register('medicalConditions')} /></div>
      <div className="grid gap-1.5"><Label htmlFor="allergies">Alergias</Label><Textarea id="allergies" {...form.register('allergies')} /></div>
      <div className="grid gap-3 sm:grid-cols-2"><div className="grid gap-1.5"><Label htmlFor="behaviorWithDogs">Comportamiento con otros perros</Label><Textarea id="behaviorWithDogs" {...form.register('behaviorWithDogs')} /></div><div className="grid gap-1.5"><Label htmlFor="behaviorWithPeople">Comportamiento con personas</Label><Textarea id="behaviorWithPeople" {...form.register('behaviorWithPeople')} /></div></div>
      <div className="grid gap-1.5"><Label htmlFor="careSpecifications">Especificaciones para el cuidado</Label><Textarea id="careSpecifications" {...form.register('careSpecifications')} /></div>
      <div className="grid gap-1.5"><Label htmlFor="ownerComments">Comentarios del propietario</Label><Textarea id="ownerComments" {...form.register('ownerComments')} /></div>
    </>
  );
}

const careStatuses = [
  { value: 'danger', label: 'Peligro', className: 'border-red-300 bg-red-50 text-red-800' },
  { value: 'supervised', label: 'Supervisado', className: 'border-orange-300 bg-orange-50 text-orange-800' },
  { value: 'caution', label: 'Precaución', className: 'border-amber-300 bg-amber-50 text-amber-800' },
  { value: 'sociable', label: 'Sociable', className: 'border-green-300 bg-green-50 text-green-800' },
  { value: 'shy', label: 'Tímido', className: 'border-sky-300 bg-sky-50 text-sky-800' },
  { value: 'senior', label: 'Viejito', className: 'border-violet-300 bg-violet-50 text-violet-800' },
  { value: 'other', label: 'Otro', className: 'border-slate-300 bg-slate-50 text-slate-700' },
] as const;
